/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import GetUserMessage from './messages/getusermessage';
import GetUserResponse from './responses/getuserresponse';

import MessagesCompressor from './../messagescompressor';
import GetManyUsersMessage from './messages/getmanyusersmessage';
import GetManyUsersResponse from './responses/getmanyusersresponse';
import { IWebSocketGateway } from '../websocketgateway/websocketgateway';

interface IUserData extends Record<string, unknown>{
	id?: string;
}

/**
 * Represents User entity.
 */
class User {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	public static readonly _SERVICE: number = 2;

	public declare readonly id?: string;

	private declare readonly _attributes: Map<string, unknown>;

	/**
	 * Creates User object. Passed attributes will be set on instance as a read-only attributes.
	 * Passing undefined or empty object will create "Anonymous" object.
	 */
	public constructor( attributes: IUserData = {} ) {
		this._attributes = new Map();

		for ( const name of Object.keys( attributes ) ) {
			if ( name === 'id' ) {
				this.id = attributes.id;

				continue;
			}

			this._attributes.set( name, attributes[ name ] );

			Object.defineProperty( this, name, {
				enumerable: true,
				configurable: false,
				get: () => this._attributes.get( name )
			} );
		}
	}

	/**
	 * Creates User from data object, ie. from token data.
	 *
	 *     const user = User.fromData( { id: '1234', name: 'Bob' } );
	 *
	 *     // Anonymous user will have no attributes.
	 *     const anonymous = User.fromData();
	 */
	public static fromData( data: IUserData ): User {
		return new User( data );
	}

	/**
	 * Returns existing User from server or create a User object.
	 *
	 *     const user = await User.get( wsGateway, '1234' );
	 */
	public static async get( wsGateway: IWebSocketGateway, id: string ): Promise<User> {
		const message: GetUserMessage = new GetUserMessage( id );

		try {
			const serviceResponse: Buffer = await wsGateway._sendRequest(
				User._SERVICE,
				GetUserMessage.TYPE,
				MessagesCompressor.encode( message )
			);

			const getUserResponse: GetUserResponse = MessagesCompressor.decode( serviceResponse, GetUserResponse ) as GetUserResponse;

			return new User( getUserResponse.attributes );
		} catch ( error ) {
			return new User( { id } );
		}
	}

	/**
	 * Returns existing Users from server or create a User objects.
	 *
	 *     const users = await User.getMany( wsGateway, ['1234', '5678'] );
	 */
	public static async getMany( wsGateway: IWebSocketGateway, ids: string[] ): Promise<User[]> {
		const message: GetManyUsersMessage = new GetManyUsersMessage( ids );

		try {
			const serviceResponse: Buffer = await wsGateway._sendRequest(
				User._SERVICE,
				GetManyUsersMessage.TYPE,
				MessagesCompressor.encode( message )
			);

			const response: GetManyUsersResponse = MessagesCompressor.decode(
				serviceResponse,
				GetManyUsersResponse
			) as GetManyUsersResponse;

			return response.users.map( user => new User( user ) );
		} catch ( error ) {
			return ids.map( id => new User( { id } ) );
		}
	}
}

// It allows to setting a signature index for classes that TS natively does not allow for.
// The behavior is handled in the class constructor.
// eslint-disable-next-line no-redeclare, @typescript-eslint/naming-convention
declare interface User extends Record<string, unknown> {
}

export default User;
